import {useEffect, useRef} from "react";
import BoltIcon from '@mui/icons-material/Bolt';
import {motion, useAnimation, useInView, useScroll, useTransform} from "framer-motion";
import {Box, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
// Sample project data

const ProjectCard = ({project}) => {
    const navigate = useNavigate();

    const ref = useRef(null);
    const controls = useAnimation();
    const isInView = useInView(ref, { once: false, amount: 0.2 }); // Detects if it's in view

    useEffect(() => {
        if (isInView) {
            controls.start({ opacity: 1, y: 0 });
        }
    }, [isInView, controls]); // Runs when the view state changes


    const {scrollYProgress} = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });

    const yTransform = useTransform(scrollYProgress, [0, 1], [project.parallax1, project.parallax2]);

    return (
        <motion.div
            ref={ref}
            whileHover={{scale: 1.05}}
            transition={{duration: 0.3}}
            initial={{ opacity: 0, y: 50 }}
            animate={controls} // Now controlled by `useAnimation`
            whileInView={{ opacity: 1, y: 0 }}
        >
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    display: "flex",
                    borderRadius: "12px",
                    boxShadow: "0px 0px 15px rgba(255, 215, 0, 0.1)",
                    "&:hover": {
                        boxShadow: "0px 0px 25px rgba(255, 215, 0, 0.2)",
                    },
                    "&:hover .lightningTop": {
                        opacity: { xs: "0 !important", sm: "1 !important" },
                        transform: { xs: "none !important", sm: "translate(-10px, -10px) !important" },
                    },
                    "&:hover .lightningBottom": {
                        opacity: { xs: "0 !important", sm: "1 !important" },
                        transform: { xs: "none !important", sm: "translate(10px, 10px) !important" },
                    },
                    cursor: 'pointer',
                }}
                onClick={() => navigate(project.link)}

            >
                <Box
                    sx={{
                        width: "100%",
                        borderRadius: "12px",
                        maxHeight: {xs: "150px", sm: "200px", md: "250px", lg: "300px"},
                        minHeight: {xs: "150px", sm: "200px", md: "250px", lg: "300px"},
                        background: "#1e1e1e",
                        overflow: "hidden",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        display: "flex",
                    }}
                >
                    {/* Project Image */}
                    <motion.img
                        src={project.image}
                        alt={project.title}
                        style={{
                            width: "100%",
                            height: "auto",
                            minHeight: '100%',
                            objectFit: "cover",
                            borderRadius: "8px",
                            transformOrigin: "center",
                            translateY: yTransform, // Moves up/down centered

                        }}
                    />
                    {/* Project Title in Bottom Left */}
                    <Typography
                        variant="h6"
                        sx={{
                            position: "absolute",
                            bottom: "10px",
                            left: "10px",
                            backgroundColor: "rgba(0, 0, 0, 0.75)", // Black background
                            color: "white",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            fontSize: {xs: "14px", md: "16px"},
                            fontWeight: "bold",
                            border: "2px solid black",
                        }}
                    >
                        {project.left}
                    </Typography>
                    {project.right && (
                        <Typography
                            variant="h6"
                            sx={{
                                position: "absolute",
                                top: "10px",
                                right: "10px",
                                backgroundColor: "rgba(0, 0, 0, 0.75)", // Black background
                                color: "white",
                                padding: "6px 12px",
                                borderRadius: "6px",
                                fontSize: {xs: "14px", md: "16px"},
                                fontWeight: "bold",
                                border: "2px solid black",
                            }}
                        >
                            {project.right}
                        </Typography>
                    )}
                </Box>
                {/* Lightning Effects */}
                <motion.div
                    className="lightningTop"
                    style={{
                        position: "absolute",
                        top: "-30px",
                        left: "-30px",
                        width: "100px",
                        height: "100px",
                        opacity: 0,
                        transition: "opacity 0.3s, transform 0.3s",
                        color: "goldenrod",
                    }}
                >
                    <BoltIcon
                        sx={{
                            width: "100%",
                            height: "100%",
                        }}
                    />
                </motion.div>
                <motion.div
                    className="lightningBottom"
                    style={{
                        position: "absolute",
                        bottom: "-30px",
                        right: "-30px",
                        width: "100px",
                        height: "100px",
                        opacity: 0,
                        transition: "opacity 0.3s, transform 0.3s",
                        color: "goldenrod",
                    }}
                >
                    <BoltIcon
                        sx={{
                            width: "100%",
                            height: "100%",
                        }}
                    />
                </motion.div>
            </Box>
        </motion.div>
    );
};

export default ProjectCard;